import { useState, useMemo, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'

import Button from 'design-system/button'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import { EnrolState } from 'enum/enrol-state.enum'
import { MonitoringStatus } from 'enum'
import { ModalBody } from './ModalBody'
import { IEnrolData, Values } from './types'
import { prefixButtons } from './ModalBody/forms/constants'

interface ParticipantsFormProps {
  show: boolean
  onHide: () => void
  onSave: (values: Values) => void
  data: IEnrolData | null
}

export const ParticipantsForm = ({
  show,
  onHide,
  onSave,
  data,
}: ParticipantsFormProps) => {
  const { t } = useTranslation()

  const initial = useMemo<Values>(() => {
    const { finalState, attendance, assessments } = {
      ...data?.synchronousCourseEnrol,
    }
    return {
      state: data?.advance,
      finalState: finalState ?? EnrolState.Pending,
      attendance: attendance || 0,
      notes: assessments?.map(({ qualification }) => qualification.toString()),
    }
  }, [data])

  const [values, setValues] = useState<Values>(initial)
  const isValid = useMemo(() => {
    const { state, finalState, attendance, notes } = values
    const valid = EnrolState.Pending !== finalState ? !!notes?.length : true
    return (
      MonitoringStatus[state ?? -1] &&
      EnrolState[finalState ?? -1] &&
      typeof attendance === 'number' &&
      attendance >= 0 &&
      attendance <= 100 &&
      !!notes?.every((note) => note !== '') &&
      valid
    )
  }, [values])

  useEffect(() => {
    setValues(initial)
  }, [initial])

  const setValue = <K extends keyof Values>(key: K, value: Values[K]) => {
    if (key === 'attendance') {
      const max = 100
      const min = 0
      setValues({
        ...values,
        [key]:
          typeof value === 'number' &&
          (value < max ? (value < min ? min : value) : max),
      })
    } else setValues({ ...values, [key]: value })
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton className="ml-3 mr-3 mt-2 border-bottom-0">
        <Modal.Title className="d-block d-md-none">
          <p>{t('administration.monitor.finishCourse.title')}</p>
        </Modal.Title>
      </Modal.Header>
      {data && <ModalBody data={data} values={values} setValue={setValue} />}
      <Modal.Footer className="ml-3 mr-3 mb-2 border-top-0">
        <Button
          size={ButtonSizes.Small}
          className="mr-2"
          variant={ButtonVariant.Secondary}
          onClick={onHide}
        >
          {t('commons.buttons.cancel')}
        </Button>
        <Button
          disabled={!isValid}
          size={ButtonSizes.Small}
          onClick={() => onSave(values)}
        >
          {t(`${prefixButtons}.save`)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ParticipantsForm
